import React, { useEffect } from "react";
import { Button, Checkbox, Drawer, Empty, Form, Input, Radio, Spin } from "antd";
import { getQuestionFieldName, getSpecifyFieldName, normalizeOptions } from "./surveyUtils";

// Ported from the old app's surveySubmissionForm/QuestionField.js +
// DynamicSurveyForm.js. antd 4.10 has no Form.useWatch, so the "please
// specify" follow-up re-renders through a shouldUpdate Form.Item instead.

const isRequiredQuestion = (question) => question.is_required === 1 || question.is_required === true || question.is_required === "1";

const QuestionField = ({ question = {}, index = 0, readOnly = false }) => {
  const name = getQuestionFieldName(question, index);
  const specifyName = getSpecifyFieldName(question, index);

  const options = normalizeOptions(question.options);
  const required = isRequiredQuestion(question);
  const questionType = question.question_type || "text";

  const renderControl = () => {
    switch (questionType) {
      case "textarea":
        return <Input.TextArea rows={3} placeholder="Enter your answer" disabled={readOnly} />;
      case "radio":
        return (
          <Radio.Group disabled={readOnly}>
            {options.map((option) => (
              <Radio key={option.value} value={option.value} style={{ display: "block", lineHeight: "32px" }}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );
      case "checkbox":
        return (
          <Checkbox.Group disabled={readOnly}>
            {options.map((option) => (
              <Checkbox key={option.value} value={option.value} style={{ display: "block", marginLeft: 0, lineHeight: "32px" }}>
                {option.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        );
      case "text":
      default:
        return <Input placeholder="Enter your answer" disabled={readOnly} />;
    }
  };

  return (
    <>
      <Form.Item
        label={<span>{index + 1}. {question.question_text || "Untitled question"}</span>}
        name={name}
        extra={question.question_help_text || undefined}
        rules={required ? [{ required: true, message: "This question is required." }] : []}
      >
        {renderControl()}
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prev, cur) => prev[name] !== cur[name]}>
        {({ getFieldValue }) => {
          const currentValue = getFieldValue(name);
          const specifyOption = options.find((option) => {
            if (!option.requires_specify) return false;
            if (questionType === "checkbox") return Array.isArray(currentValue) && currentValue.includes(option.value);
            return currentValue === option.value;
          });

          return specifyOption ? (
            <Form.Item
              label={`Please specify (${specifyOption.label})`}
              name={specifyName}
              rules={readOnly ? [] : [{ required: true, message: "Please specify." }]}
              style={{ marginLeft: 24 }}
            >
              <Input placeholder="Please specify" disabled={readOnly} />
            </Form.Item>
          ) : null;
        }}
      </Form.Item>
    </>
  );
};

// Turn raw form values into the flat answer array the backend stores.
// `answer_text` is the human readable answer (what the PDF prints),
// `answer_json` keeps the structured value for anything that needs to re-read it.
const buildAnswers = (questions, values) =>
  questions.map((question, index) => {
    const name = getQuestionFieldName(question, index);
    const specifyName = getSpecifyFieldName(question, index);

    const rawValue = values[name];
    const specifyValue = values[specifyName];
    const options = normalizeOptions(question.options);
    const questionType = question.question_type || "text";

    let answerText = "";
    let answerJson = null;

    if (questionType === "checkbox") {
      const selected = Array.isArray(rawValue) ? rawValue : [];
      const labels = selected.map((value) => {
        const option = options.find((item) => item.value === value);
        return option ? option.label : `${value}`;
      });
      const needsSpecify = selected.some((value) => {
        const option = options.find((item) => item.value === value);
        return !!(option && option.requires_specify);
      });

      answerText = labels.join(", ");
      if (needsSpecify && specifyValue) answerText = `${answerText} (${specifyValue})`;
      answerJson = { values: selected, specify: needsSpecify && specifyValue ? specifyValue : "" };
    } else if (questionType === "radio") {
      const option = options.find((item) => item.value === rawValue);
      const needsSpecify = !!(option && option.requires_specify);

      answerText = option ? option.label : rawValue === undefined || rawValue === null ? "" : `${rawValue}`;
      if (needsSpecify && specifyValue) answerText = `${answerText} (${specifyValue})`;
      answerJson = { value: rawValue === undefined || rawValue === null ? "" : rawValue, specify: needsSpecify && specifyValue ? specifyValue : "" };
    } else {
      answerText = rawValue === undefined || rawValue === null ? "" : `${rawValue}`;
    }

    return {
      question_id: question.id !== undefined ? question.id : null,
      question_text: question.question_text || "",
      question_type: questionType,
      answer_text: answerText,
      answer_json: answerJson,
    };
  });

// Facility/session agnostic renderer for a survey template, used both by the
// real submission screen and by every preview, so the two can never diverge.
// Deliberately one <Form> plus a .map() over the template's questions (not
// Form.List) — the question set is a fixed, heterogeneous list defined by the
// template. Makes no API calls of its own.
const DynamicSurveyForm = ({ template = {}, submitting = false, submitLabel = "Submit", readOnly = false, initialValues = {}, onSubmit, showSubmitButton = true }) => {
  const [form] = Form.useForm();

  const questions = Array.isArray(template.questions) ? template.questions : [];

  useEffect(() => {
    // the <Form> only mounts when there are questions; skip the reset otherwise
    // so antd doesn't warn about an unconnected form instance
    if (questions.length > 0) form.resetFields();
  }, [template.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinish = (values) => {
    if (readOnly || typeof onSubmit !== "function") return;
    onSubmit(buildAnswers(questions, values), values);
  };

  return (
    <div>
      {template.name || template.survey_type ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #E6E9EE" }}>
          <h4 style={{ margin: 0, color: "#101828" }}>{template.name || ""}</h4>
          {template.survey_type ? <span style={{ color: "#5B6472" }}>{template.survey_type}</span> : null}
        </div>
      ) : null}

      {template.description ? <p style={{ whiteSpace: "pre-wrap", marginBottom: 16, color: "#101828" }}>{template.description}</p> : null}

      {questions.length === 0 ? (
        <Empty description="This survey has no questions yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Form form={form} layout="vertical" initialValues={initialValues} onFinish={handleFinish} scrollToFirstError>
          {questions.map((question, index) => (
            <QuestionField key={getQuestionFieldName(question, index)} question={question} index={index} readOnly={readOnly} />
          ))}

          {showSubmitButton && !readOnly ? (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {submitLabel}
              </Button>
            </Form.Item>
          ) : null}
        </Form>
      )}
    </div>
  );
};

// Renders whatever the builder currently holds through the exact same
// component the real submission screen uses, so preview and submission can
// never diverge.
export const SurveyPreviewDrawer = ({ visible = false, onClose, template = null, loading = false }) => (
  <Drawer title="Survey Preview" placement="right" width={720} visible={visible} onClose={onClose} destroyOnClose>
    <Spin spinning={loading}>
      {template ? <DynamicSurveyForm template={template} readOnly showSubmitButton={false} /> : <Empty description="Nothing to preview yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Spin>
  </Drawer>
);

export default DynamicSurveyForm;
